/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — canvas pane right-click menu (People Rebuild §3
   "canvas pane menu with view actions your call"). View-level
   actions only: fit view, auto-arrange (edit-gated), snap toggle,
   show/hide all team boxes.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Maximize2, LayoutGrid, Magnet, Eye, EyeOff, Type, CircleDot } from 'lucide-react';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';

export interface CanvasContextMenuProps {
  x: number;
  y: number;
  canEdit: boolean;
  snapEnabled: boolean;
  onFitView: () => void;
  onAutoArrange: () => void;
  onToggleSnap: () => void;
  onShowAllTeamBoxes: () => void;
  onHideAllTeamBoxes: () => void;
  /** Create a session-local text box at this menu's click position
   *  (People Rebuild, Josh grant 2026-08-17). Edit-gated like
   *  auto-arrange — omitted entirely in view mode. */
  onAddTextBox?: () => void;
  /** Create a free-standing junction at this menu's click position
   *  (Josh 2026-08-18: junctions are independent anchors, not line
   *  properties — creation must not require an existing line). */
  onAddJunction?: () => void;
  onClose: () => void;
}

export function CanvasContextMenu({
  x,
  y,
  canEdit,
  snapEnabled,
  onFitView,
  onAutoArrange,
  onToggleSnap,
  onShowAllTeamBoxes,
  onHideAllTeamBoxes,
  onAddTextBox,
  onAddJunction,
  onClose,
}: CanvasContextMenuProps) {
  const items: { label: string; icon: React.ReactNode; action: () => void }[] = [
    {
      label: 'Fit view',
      icon: <Icon icon={Maximize2} em={1.0769} aria-hidden="true" />,
      action: onFitView,
    },
  ];
  if (canEdit) {
    items.push({
      label: 'Auto-arrange',
      icon: <Icon icon={LayoutGrid} em={1.0769} aria-hidden="true" />,
      action: onAutoArrange,
    });
    if (onAddTextBox) {
      items.push({
        label: 'Add text box',
        icon: <Icon icon={Type} em={1.0769} aria-hidden="true" />,
        action: onAddTextBox,
      });
    }
    if (onAddJunction) {
      items.push({
        label: 'Add junction',
        icon: <Icon icon={CircleDot} em={1.0769} aria-hidden="true" />,
        action: onAddJunction,
      });
    }
  }
  items.push({
    label: snapEnabled ? 'Turn snap off' : 'Turn snap on',
    icon: <Icon icon={Magnet} em={1.0769} aria-hidden="true" />,
    action: onToggleSnap,
  });
  items.push({
    label: 'Show all team boxes',
    icon: <Icon icon={Eye} em={1.0769} aria-hidden="true" />,
    action: onShowAllTeamBoxes,
  });
  items.push({
    label: 'Hide all team boxes',
    icon: <Icon icon={EyeOff} em={1.0769} aria-hidden="true" />,
    action: onHideAllTeamBoxes,
  });

  return (
    <div
      data-orgchart-menu
      role="menu"
      aria-label="Chart view options"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: rem(8),
        boxShadow: '0 8px 24px rgba(0,0,0,.18)',
        padding: 'var(--space-1) 0',
        minWidth: rem(190),
        zIndex: 1000,
      }}
    >
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => {
            onClose();
            item.action();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            textAlign: 'left',
            padding: rem(7) + ' ' + rem(14),
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--tx)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}
